import React from "react";
import Helmet from "react-helmet";
import PropTypes from "prop-types";

import { courseSeoByPath } from "../data/courseSeo";

const siteUrl = "https://barcelonacodeschool.com";
const organizationId = `${siteUrl}/#organization`;
const websiteId = `${siteUrl}/#website`;
const defaultImageWidth = 1801;
const defaultImageHeight = 900;

const toAbsoluteUrl = (url) => {
  if (!url || /^https?:\/\//i.test(url)) return url;
  return `${siteUrl}${url.startsWith("/") ? url : `/${url}`}`;
};

const normalizePath = (path) => {
  if (!path) return "";

  const cleanPath = path.split("?")[0].split("#")[0];
  return cleanPath === "/" ? cleanPath : `${cleanPath.replace(/\/+$/, "")}/`;
};

const buildOffer = (course, canonicalUrl) => {
  if (!course.price && !course.lowPrice) return undefined;

  if (course.lowPrice && course.highPrice) {
    return {
      "@type": "AggregateOffer",
      url: canonicalUrl,
      priceCurrency: course.priceCurrency || "EUR",
      lowPrice: String(course.lowPrice),
      highPrice: String(course.highPrice),
      offerCount: String(course.offerCount || 2),
    };
  }

  return {
    "@type": "Offer",
    url: canonicalUrl,
    price: String(course.price),
    priceCurrency: course.priceCurrency || "EUR",
  };
};

const buildLocations = (course, canonicalUrl) => {
  const locations = [];
  const courseModes = course.courseMode || [];

  if (courseModes.some((mode) => /campus|person|office|onsite/i.test(mode))) {
    locations.push({
      "@type": "Place",
      name: "Barcelona Code School",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Carrer de Paris 157",
        postalCode: "08036",
        addressLocality: "Barcelona",
        addressRegion: "Catalonia",
        addressCountry: "ES",
      },
    });
  }

  if (courseModes.some((mode) => /online|remote/i.test(mode))) {
    locations.push({ "@type": "VirtualLocation", url: canonicalUrl });
  }

  return locations;
};

const buildCourseInstances = (course, canonicalUrl, offer) => {
  if (!course.instances || !course.instances.length) return undefined;

  const defaultLocations = buildLocations(course, canonicalUrl);

  return course.instances.map((instance) => ({
    "@type": "CourseInstance",
    name: instance.name || `${course.name} — ${instance.startDate}`,
    startDate: instance.startDate,
    ...(instance.endDate && { endDate: instance.endDate }),
    ...(instance.duration || course.duration
      ? { duration: instance.duration || course.duration }
      : {}),
    courseMode: instance.courseMode || course.courseMode,
    ...(instance.courseWorkload || course.courseWorkload
      ? { courseWorkload: instance.courseWorkload || course.courseWorkload }
      : {}),
    inLanguage: course.language || "en",
    ...(defaultLocations.length ? { location: defaultLocations } : {}),
    ...(offer ? { offers: offer } : {}),
  }));
};

const buildCourseGraph = (course, canonicalUrl) => {
  const pageId = `${canonicalUrl}#webpage`;
  const primaryImageId = `${canonicalUrl}#primaryimage`;
  const breadcrumbsId = `${canonicalUrl}#breadcrumbs`;
  const entityId = `${canonicalUrl}#course`;
  const offer = buildOffer(course, canonicalUrl);
  const courseInstances = buildCourseInstances(course, canonicalUrl, offer);
  const faqId = `${canonicalUrl}#faq`;
  const breadcrumbItems = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Barcelona Code School",
      item: `${siteUrl}/`,
    },
    ...(course.categoryUrl && course.categoryUrl !== canonicalUrl
      ? [
          {
            "@type": "ListItem",
            position: 2,
            name: course.breadcrumbCategory || "Courses",
            item: course.categoryUrl,
          },
        ]
      : []),
  ];

  breadcrumbItems.push({
    "@type": "ListItem",
    position: breadcrumbItems.length + 1,
    name: course.name,
    item: canonicalUrl,
  });

  const entity =
    course.schemaType === "Service"
      ? {
          "@type": "Service",
          "@id": entityId,
          name: course.name,
          description: course.description,
          url: canonicalUrl,
          image: course.image,
          provider: { "@id": organizationId },
          serviceType: course.serviceType,
          keywords: course.keywords.join(", "),
          areaServed: course.areaServed || ["Barcelona", "Spain", "Europe"],
          availableChannel: [
            {
              "@type": "ServiceChannel",
              serviceUrl: canonicalUrl,
              availableLanguage: course.languages || [course.language || "en"],
            },
          ],
          audience: {
            "@type": "BusinessAudience",
            audienceType: course.audience,
          },
          ...(course.serviceCatalog && {
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: course.serviceCatalogName || `${course.name} services`,
              itemListElement: course.serviceCatalog.map((service) => ({
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: service.name,
                  description: service.description,
                  serviceType: service.serviceType || service.name,
                  provider: { "@id": organizationId },
                },
              })),
            },
          }),
        }
      : {
          "@type": "Course",
          "@id": entityId,
          name: course.name,
          ...(course.alternateName && { alternateName: course.alternateName }),
          description: course.description,
          url: canonicalUrl,
          image: course.image,
          provider: { "@id": organizationId },
          inLanguage: course.language || "en",
          ...(course.courseMode && { courseMode: course.courseMode }),
          ...(course.duration && { timeRequired: course.duration }),
          ...(course.educationalLevel && {
            educationalLevel: course.educationalLevel,
          }),
          ...(course.prerequisites && {
            coursePrerequisites: course.prerequisites,
          }),
          ...(course.credential && {
            educationalCredentialAwarded: course.credential,
          }),
          learningResourceType: course.learningResourceType || "Course",
          ...(course.audience && {
            audience: {
              "@type": "EducationalAudience",
              audienceType: course.audience,
            },
          }),
          ...(course.ageRange && { typicalAgeRange: course.ageRange }),
          ...(course.teaches && { teaches: course.teaches }),
          keywords: course.keywords.join(", "),
          ...(offer && { offers: offer }),
          ...(courseInstances && { hasCourseInstance: courseInstances }),
        };

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "EducationalOrganization",
        "@id": organizationId,
        name: "Barcelona Code School",
        url: `${siteUrl}/`,
        logo: {
          "@type": "ImageObject",
          url: `${siteUrl}/files/pics/barcelona_code_school_logo.png`,
        },
        email: "office@barcelonacodeschool.com",
        telephone: "+34 936 639 807",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Carrer de Paris 157",
          postalCode: "08036",
          addressLocality: "Barcelona",
          addressRegion: "Catalonia",
          addressCountry: "ES",
        },
      },
      {
        "@type": "WebSite",
        "@id": websiteId,
        url: `${siteUrl}/`,
        name: "Barcelona Code School",
        publisher: { "@id": organizationId },
        inLanguage: ["en", "es"],
      },
      {
        "@type": "WebPage",
        "@id": pageId,
        url: canonicalUrl,
        name: course.name,
        description: course.description,
        isPartOf: { "@id": websiteId },
        about: { "@id": entityId },
        mainEntity: { "@id": entityId },
        breadcrumb: { "@id": breadcrumbsId },
        primaryImageOfPage: { "@id": primaryImageId },
        inLanguage: course.language || "en",
        ...(course.dateModified && { dateModified: course.dateModified }),
        ...(course.faqs && course.faqs.length
          ? {
              hasPart: { "@id": faqId },
            }
          : {}),
      },
      {
        "@type": "ImageObject",
        "@id": primaryImageId,
        url: course.image,
        contentUrl: course.image,
        width: course.imageWidth || defaultImageWidth,
        height: course.imageHeight || defaultImageHeight,
        caption: course.imageAlt,
      },
      entity,
      {
        "@type": "BreadcrumbList",
        "@id": breadcrumbsId,
        itemListElement: breadcrumbItems,
      },
      ...(course.faqs && course.faqs.length
        ? [
            {
              "@type": "FAQPage",
              "@id": faqId,
              url: `${canonicalUrl}#frequently-asked-questions`,
              mainEntity: course.faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.answer,
                },
              })),
            },
          ]
        : []),
    ],
  };
};

const CourseSeo = ({ path, location }) => {
  const normalizedPath = normalizePath((location && location.pathname) || path);
  const courseConfig = courseSeoByPath[normalizedPath];

  if (!courseConfig) return null;

  const canonicalUrl = `${siteUrl}${normalizedPath}`;
  const course = {
    ...courseConfig,
    image: toAbsoluteUrl(courseConfig.image),
  };
  const imageWidth = course.imageWidth || defaultImageWidth;
  const imageHeight = course.imageHeight || defaultImageHeight;
  const imageType = course.imageType || "image/jpeg";
  const alternates = course.alternates || [];
  const hasDefaultAlternate = alternates.some(
    (link) => link.hrefLang === "x-default",
  );
  const links = [
    { rel: "canonical", href: canonicalUrl },
    { rel: "alternate", hrefLang: course.language || "en", href: canonicalUrl },
    ...(!hasDefaultAlternate
      ? [{ rel: "alternate", hrefLang: "x-default", href: canonicalUrl }]
      : []),
    { rel: "describedby", type: "text/markdown", href: "/llms.txt" },
    ...alternates,
  ];

  return (
    <Helmet
      title={course.name}
      htmlAttributes={{ lang: course.language || "en" }}
      meta={[
        { name: "description", content: course.description },
        { name: "keywords", content: course.keywords.join(", ") },
        {
          name: "robots",
          content:
            "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
        },
        {
          name: "googlebot",
          content:
            "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
        },
        { name: "author", content: "Barcelona Code School" },
        { name: "application-name", content: "Barcelona Code School" },
        { name: "theme-color", content: "#000000" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: course.locale || "en_US" },
        { property: "og:site_name", content: "Barcelona Code School" },
        { property: "og:title", content: course.name },
        {
          property: "og:description",
          content: course.socialDescription || course.description,
        },
        { property: "og:url", content: canonicalUrl },
        { property: "og:image", content: course.image },
        { property: "og:image:secure_url", content: course.image },
        { property: "og:image:type", content: imageType },
        { property: "og:image:width", content: String(imageWidth) },
        { property: "og:image:height", content: String(imageHeight) },
        { property: "og:image:alt", content: course.imageAlt },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: course.name },
        {
          name: "twitter:description",
          content: course.socialDescription || course.description,
        },
        { name: "twitter:image", content: course.image },
        { name: "twitter:image:alt", content: course.imageAlt },
      ]}
      link={links}
      script={
        course.structuredData === false
          ? []
          : [
              {
                type: "application/ld+json",
                innerHTML: JSON.stringify(
                  buildCourseGraph(course, canonicalUrl),
                ),
              },
            ]
      }
    />
  );
};

CourseSeo.propTypes = {
  path: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
};

export default CourseSeo;
